import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@daycal/ui';
import { 
  FiCalendar, FiRotateCcw, FiShield, FiCheckCircle, 
  FiAlertTriangle, FiCreditCard, FiClock, FiMail, FiArrowRight,
  FiFileText, FiRefreshCw, FiHelpCircle
} from 'react-icons/fi';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { useLegalCopy } from '../lib/legalContent.js';
import RichText from '../components/RichText.jsx';

function Logo() {
  return (
    <Link to="/" className="group flex items-center">
      <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 group-hover:scale-105" />
    </Link>
  );
}

function LangToggle({ locale, setLocale }) {
  return (
    <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
      {[['en', 'ENG'], ['bn', 'BN']].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          className={`rounded-full px-2.5 py-1 transition-colors ${
            locale === code ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function Refund() {
  const { locale, setLocale } = useLocale();
  const t = useLegalCopy('refund', locale);
  const [activeSection, setActiveSection] = useState('overview');

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between">
      <SEO 
        path="/refund" 
        title={`${t.title} - DayCal`} 
        description={t.subtitle} 
      />
      
      {/* Sticky Navigation Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Logo />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <Link to="/" className="hover:text-slate-900 transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="text-brand-600 font-semibold">Refund Policy</Link>
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle locale={locale} setLocale={setLocale} />
            <Link to="/login" className="rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-slate-800">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-100/70 border-b border-slate-200/60 py-16">
        <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-60" />
        <div className="pointer-events-none absolute -right-20 top-0 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-5 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50/90 px-3.5 py-1 text-xs font-semibold text-emerald-800 shadow-sm">
            <FiRotateCcw className="h-3.5 w-3.5 text-emerald-600" /> {t.badge}
          </span>
          <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {t.title}
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
            {t.subtitle}
          </p>
          <p className="mt-3 text-xs font-medium text-slate-400">
            Last Updated: <span className="font-semibold text-slate-600">{t.lastUpdated}</span>
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <main className="mx-auto max-w-6xl px-5 py-14 flex-1">
        
        {/* Gateway Fast-Facts Callout */}
        <div className="mb-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-dc border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-wider mb-1">
              <FiClock className="h-4 w-4" /> {t.facts.card1.label}
            </div>
            <p className="text-sm font-semibold text-slate-900">{t.facts.card1.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.facts.card1.sub}</p>
          </div>

          <div className="rounded-dc border border-brand-200 bg-brand-50/60 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-brand-700 font-bold text-xs uppercase tracking-wider mb-1">
              <FiRotateCcw className="h-4 w-4" /> {t.facts.card2.label}
            </div>
            <p className="text-sm font-semibold text-slate-900">{t.facts.card2.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.facts.card2.sub}</p>
          </div>

          <div className="rounded-dc border border-indigo-200 bg-indigo-50/60 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-indigo-700 font-bold text-xs uppercase tracking-wider mb-1">
              <FiCreditCard className="h-4 w-4" /> {t.facts.card3.label}
            </div>
            <p className="text-sm font-semibold text-slate-900">{t.facts.card3.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.facts.card3.sub}</p>
          </div>

          <div className="rounded-dc border border-purple-200 bg-purple-50/60 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-purple-700 font-bold text-xs uppercase tracking-wider mb-1">
              <FiShield className="h-4 w-4" /> {t.facts.card4.label}
            </div>
            <p className="text-sm font-semibold text-slate-900">{t.facts.card4.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.facts.card4.sub}</p>
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-12">
          
          {/* Index Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-dc border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                <FiFileText className="h-4 w-4 text-brand-600" /> {t.sidebar.indexTitle}
              </h3>
              <nav className="space-y-1">
                {t.toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className={`w-full text-left px-3 py-2 text-xs font-semibold rounded-dc-sm transition-all flex items-center justify-between ${
                      activeSection === item.id 
                        ? 'bg-brand-50 text-brand-700 font-bold border-l-2 border-brand-600' 
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span>{item.title}</span>
                    {activeSection === item.id && <FiArrowRight className="h-3.5 w-3.5 text-brand-600" />}
                  </button>
                ))}
              </nav>

              <div className="mt-6 pt-6 border-t border-slate-100 bg-slate-50/60 p-4 rounded-dc-sm">
                <p className="text-xs text-slate-500 font-medium">{t.sidebar.helpText}</p>
                <Link to="/contact" className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 hover:underline">
                  <FiMail className="h-3.5 w-3.5" /> {t.sidebar.helpLink} &rarr;
                </Link>
              </div>
            </div>
          </aside>

          {/* Policy Detail Sections */}
          <div className="lg:col-span-8 space-y-10">
            
            {/* Section 1: Overview */}
            <section id="overview" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-brand-50 text-brand-600">
                  <FiCheckCircle className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.overview.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.overview.p1} />
              <RichText className="mt-3 text-slate-600 leading-relaxed text-sm" html={t.sections.overview.p2} />
              <div className="mt-4 grid gap-3 sm:grid-cols-2 text-xs text-slate-700">
                <div className="p-3.5 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <span className="font-bold text-brand-700 uppercase tracking-wide block mb-1">{t.sections.overview.boxATitle}</span>
                  {t.sections.overview.boxABody}
                </div>
                <div className="p-3.5 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <span className="font-bold text-emerald-700 uppercase tracking-wide block mb-1">{t.sections.overview.boxBTitle}</span>
                  {t.sections.overview.boxBBody}
                </div>
              </div>
            </section>

            {/* Section 2: Subscriptions */}
            <section id="subscription-refunds" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-indigo-50 text-indigo-600">
                  <FiRefreshCw className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.subscriptionRefunds.heading}</h2>
              </div>
              <div className="space-y-4 text-sm text-slate-600 leading-relaxed">
                <div>
                  <h4 className="font-bold text-slate-800 text-base mb-1">{t.sections.subscriptionRefunds.block1Title}</h4>
                  <RichText html={t.sections.subscriptionRefunds.block1Body} />
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 text-base mb-1">{t.sections.subscriptionRefunds.block2Title}</h4>
                  <RichText html={t.sections.subscriptionRefunds.block2Body} />
                  <ul className="mt-2 space-y-1 text-xs text-slate-600 pl-4 list-disc">
                    {t.sections.subscriptionRefunds.block2Items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 text-base mb-1">{t.sections.subscriptionRefunds.block3Title}</h4>
                  <RichText html={t.sections.subscriptionRefunds.block3Body} />
                </div>
              </div>
            </section>

            {/* Section 3: Booking Cancellations */}
            <section id="booking-cancellations" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-emerald-50 text-emerald-600">
                  <FiRotateCcw className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.bookingCancellations.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.bookingCancellations.intro} />

              <div className="mt-4 space-y-3">
                <div className="p-4 rounded-dc-sm border border-emerald-100 bg-emerald-50/40 text-xs text-slate-700">
                  <h4 className="font-bold text-emerald-900 text-sm mb-1 flex items-center gap-1.5">
                    <FiCheckCircle className="h-4 w-4 text-emerald-600" /> {t.sections.bookingCancellations.card1Title}
                  </h4>
                  <RichText html={t.sections.bookingCancellations.card1Body} />
                </div>

                <div className="p-4 rounded-dc-sm border border-amber-100 bg-amber-50/40 text-xs text-slate-700">
                  <h4 className="font-bold text-amber-900 text-sm mb-1 flex items-center gap-1.5">
                    <FiAlertTriangle className="h-4 w-4 text-amber-600" /> {t.sections.bookingCancellations.card2Title}
                  </h4>
                  <RichText html={t.sections.bookingCancellations.card2Body} />
                </div>

                <div className="p-4 rounded-dc-sm border border-slate-100 bg-slate-50 text-xs text-slate-700">
                  <h4 className="font-bold text-slate-900 text-sm mb-1">{t.sections.bookingCancellations.card3Title}</h4>
                  <RichText html={t.sections.bookingCancellations.card3Body} />
                </div>
              </div>
            </section>

            {/* Section 4: Eligibility */}
            <section id="eligibility" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-purple-50 text-purple-600">
                  <FiShield className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.eligibility.heading}</h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 text-xs">
                <div className="p-4 rounded-dc-sm border border-emerald-200 bg-white">
                  <h4 className="font-bold text-emerald-700 text-sm mb-2 flex items-center gap-1.5">
                    <FiCheckCircle className="h-4 w-4 text-emerald-600" /> {t.sections.eligibility.eligibleTitle}
                  </h4>
                  <ul className="space-y-1.5 text-slate-600">
                    {t.sections.eligibility.eligibleItems.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-dc-sm border border-red-200 bg-white">
                  <h4 className="font-bold text-red-700 text-sm mb-2 flex items-center gap-1.5">
                    <FiAlertTriangle className="h-4 w-4 text-red-600" /> {t.sections.eligibility.nonRefundableTitle}
                  </h4>
                  <ul className="space-y-1.5 text-slate-600">
                    {t.sections.eligibility.nonRefundableItems.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5: Timelines & Mode of Refund */}
            <section id="timelines" className="rounded-dc border-2 border-emerald-500/80 bg-gradient-to-br from-emerald-50/30 via-white to-white p-8 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-emerald-600 text-white shadow-sm">
                  <FiCreditCard className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.timelines.heading}</h2>
                  <p className="text-xs font-semibold text-emerald-700">{t.sections.timelines.subheading}</p>
                </div>
              </div>

              <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
                <RichText html={t.sections.timelines.intro} />

                <div className="p-5 rounded-dc-sm border border-emerald-200 bg-white space-y-3 text-xs">
                  {t.sections.timelines.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <FiCheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <RichText as="div" html={item} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Section 6: Disputes & Chargebacks */}
            <section id="disputes" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-amber-50 text-amber-600">
                  <FiAlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.disputes.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.disputes.p1} />
              <RichText className="mt-3 text-slate-600 leading-relaxed text-sm" html={t.sections.disputes.p2} />
            </section>

            {/* Section 7: Contact & Escalation */}
            <section id="contact" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-brand-50 text-brand-600">
                  <FiMail className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.contact.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.contact.intro} />

              <div className="mt-4 p-5 rounded-dc-sm bg-slate-50 border border-slate-100 text-xs text-slate-700 space-y-2">
                <p><strong>{t.sections.contact.pocLabel}</strong> {t.sections.contact.pocValue}</p>
                <p><strong>{t.sections.contact.emailLabel}</strong> <a href={`mailto:${t.sections.contact.emailValue}`} className="text-brand-600 font-semibold underline">{t.sections.contact.emailValue}</a></p>
                <p><strong>{t.sections.contact.phoneLabel}</strong> <a href={`tel:${t.sections.contact.phoneValue.replace(/\s+/g, '')}`} className="text-brand-600 font-semibold underline">{t.sections.contact.phoneValue}</a>{t.sections.contact.phoneSuffix}</p>
                <p><strong>{t.sections.contact.helpdeskLabel}</strong> <Link to="/contact" className="text-brand-600 font-semibold underline">{t.sections.contact.helpdeskValue}</Link></p>
                <p><strong>{t.sections.contact.addressLabel}</strong> {t.sections.contact.addressValue}</p>
                <p><strong>{t.sections.contact.slaLabel}</strong> {t.sections.contact.slaValue}</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-xs text-slate-500 sm:flex-row">
          <Logo />
          <p>© {new Date().getFullYear()} DayCal. All rights reserved.</p>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="text-brand-600 font-bold">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
